import type {
  CSSProperties,
  FocusEvent,
  MouseEvent,
  PropsWithChildren
} from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { useWindowKeydown } from '@elfsight/lib-utils';
import type { Direction } from '../popout';
import { Popout } from '../popout';
import { LINE_HEIGHT } from './constants';
import { DropdownContext } from './DropdownContext';
import { ItemIcon } from './DropdownItem';
import { DropdownSeparator } from './DropdownSeparator';
import { getAlphaForBackground } from './getAlphaForBackground';
import type { IconSize } from './types';

export type DropdownProps = PropsWithChildren<{
  anchorElement: Element | null;
  open?: boolean;
  closeOnClick?: boolean;
  background?: string;
  fontColor?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold' | number;
  width?: string;
  alignment?: 'left' | 'center' | 'right';
  displayOver?: boolean;
  popoutDirection?: Direction;
  withoutIcons?: boolean;
  iconSize?: IconSize;
  alphaBoxShadow?: number;
  portalContainerElement?: Element;
  style?: CSSProperties;
  ariaLabel?: string;
  role?: string;
  ariaLabelledBy?: string;
  preventCloseOnAnchorElementClick?: boolean;
  onCloseRequest(event?: PointerEvent): void;
}>;

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  function Dropdown(
    {
      children,
      anchorElement,
      open = false,
      closeOnClick = true,
      background = 'rgb(255, 255, 255)',
      fontColor = 'rgb(17, 17, 17)',
      fontSize = 14,
      fontWeight = 'normal',
      width = 'auto',
      alignment = 'center',
      popoutDirection = 'downward',
      displayOver = true,
      withoutIcons = false,
      iconSize = 16,
      alphaBoxShadow = 0.3,
      portalContainerElement,
      preventCloseOnAnchorElementClick,
      role = 'listbox',
      onCloseRequest,
      style,
      ariaLabel,
      ariaLabelledBy,
      ...forwardingProps
    },
    forwardingRef
  ) {
    const separatorAlpha = getAlphaForBackground(background, 0.1);

    const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
      const nextFocusedElement = e.relatedTarget;
      const dropdownContainer = e.target.parentNode;

      const isFocusWithinRelatedElements =
        dropdownContainer?.contains(nextFocusedElement) ||
        nextFocusedElement?.parentNode?.contains(dropdownContainer);
      if (isFocusWithinRelatedElements) {
        return;
      }

      onCloseRequest();
    };

    useWindowKeydown('Escape', () => onCloseRequest());

    return (
      <Popout
        ref={forwardingRef}
        show={open}
        anchorElement={anchorElement}
        direction={popoutDirection}
        alignment={alignment}
        offsetTop={displayOver ? -4 : 4}
        portalContainerElement={portalContainerElement}
        onCloseRequest={onCloseRequest}
        preventCloseOnAnchorElementClick={preventCloseOnAnchorElementClick}
        closeOnOutsideClick
        withoutTransition
      >
        <DropdownContainer
          {...forwardingProps}
          _width={width}
          _background={background}
          _separatorAlpha={separatorAlpha}
          _fontColor={fontColor}
          _fontWeight={fontWeight}
          _fontSize={fontSize}
          _iconSize={iconSize}
          _alphaBoxShadow={alphaBoxShadow}
          role={role}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          onBlur={handleBlur}
          style={style}
        >
          <DropdownContext.Provider
            value={{
              background,
              mainFontColor: fontColor,
              closeOnClick,
              withoutIcons,
              iconSize,
              onCloseRequest
            }}
          >
            {children}
          </DropdownContext.Provider>
        </DropdownContainer>
      </Popout>
    );
  }
);

const DropdownContainer = styled.div<{
  _width: string;
  _background: string;
  _separatorAlpha: number;
  _fontColor: string;
  _fontWeight: 'normal' | 'bold' | number;
  _fontSize: number;
  _alphaBoxShadow: number;
  _iconSize: IconSize;
}>`
  min-width: 60px;
  max-width: 500px;
  padding: 8px 0;
  border-radius: 4px;
  text-align: left;
  box-shadow: 0 4px 12px 0
    rgba(0, 0, 0, ${({ _alphaBoxShadow }) => _alphaBoxShadow});
  line-height: ${LINE_HEIGHT};
  width: ${({ _width }) => _width};
  background: ${({ _background }) => _background};
  color: ${({ _fontColor }) => _fontColor};
  font-size: ${({ _fontSize }) => _fontSize}px;
  font-weight: ${({ _fontWeight }) => _fontWeight};

  ${ItemIcon} {
    padding-top: ${({ _fontSize, _iconSize }) =>
      ((_fontSize * LINE_HEIGHT - _iconSize) / 2).toFixed(1)}px;
  }

  ${DropdownSeparator} {
    opacity: ${({ _separatorAlpha }) => _separatorAlpha};
  }
`;
